import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { CalendarDays, ChevronDown, Clock, Search } from "lucide-react";
import { Modal, useModalExit } from "../../../common/comms/Modal";
import { FilterChip } from "../../../common/comms/Chip";
import { RetryBanner } from "../../../common/comms/RetryBanner";
import { SkeletonRows } from "../../../common/comms/SkeletonRows";
import { formatLocalDate, parseLocalDate } from "../../../utils/formatLocalDate";
import IntakeTimePicker from "../../../features/diet/ui/meal-slot/IntakeTimePicker";
import {
  MINUTES_PER_DAY,
  formatDayHeading,
  formatMinutesRange,
  formatShortDate,
  formatSlotRange,
  localDayStart,
  offShiftWindowError,
  parseTimeString,
  windowToInstants,
} from "./slotGrid";
import { SlotMonthCalendar } from "./SlotMonthCalendar";
import { useOffShiftBooking } from "./useOffShiftBooking";

const labelClass = "mb-1.5 block text-xs font-semibold text-bc-text";
const requiredMark = "after:ml-0.5 after:text-orange-primary after:content-['*']";

const fieldShell =
  "flex h-10 w-full items-center gap-2 rounded-lg border bg-white px-3 text-sm transition-colors focus-within:border-orange-primary focus-within:ring-2 focus-within:ring-orange-primary/20";

// The global `*:focus` outline is `!important`; a control inside a ringed
// field shell has to beat it, or it draws a grey box inside the ring.
const bareControl = "focus:!outline-none focus-visible:!outline-none";

const pad2 = (n) => String(n).padStart(2, "0");
/** Minutes since midnight → `"HH:MM"`, the 24-hour form the start picker shows. */
const toHHMM = (minutes) => `${pad2(Math.floor(minutes / 60))}:${pad2(Math.round(minutes) % 60)}`;

const initialsOf = (name) =>
  String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("") || "?";

/** A stable tint per client, from the coach ring palette. */
const avatarTone = (key) => {
  const text = String(key || "");
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) hash = (hash * 31 + text.charCodeAt(i)) >>> 0;
  const index = hash % 7;
  return `bg-coach-${index}-bg text-coach-${index}`;
};

/** "New", "3d ago", "2w ago", "1mo ago" since the client joined; null if unknown. */
const joinedAgo = (value, now = new Date()) => {
  const joined = value ? new Date(value) : null;
  if (!joined || Number.isNaN(joined.getTime())) return null;
  const days = Math.floor((now - joined) / 86400000);
  if (days < 1) return "New";
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
};

/**
 * The date field: a button showing the picked day that opens the month grid
 * in a popover. The popover is `position: fixed` against the trigger, so it
 * can hang past the modal's scrolling body instead of being clipped by it.
 */
const DateField = ({ value, today, onChange }) => {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(() => ({ year: value.getFullYear(), month: value.getMonth() }));
  const [position, setPosition] = useState(null);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  const place = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const height = popoverRef.current?.offsetHeight || 360;
    const width = popoverRef.current?.offsetWidth || 288;
    const fitsBelow = window.innerHeight - rect.bottom >= height + 12;
    const top = fitsBelow || rect.top < height + 12 ? rect.bottom + 6 : rect.top - height - 6;
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
    setPosition({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    place();
    const handleOutside = (event) => {
      if (popoverRef.current?.contains(event.target)) return;
      if (triggerRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    document.addEventListener("mousedown", handleOutside);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
      document.removeEventListener("mousedown", handleOutside);
    };
  }, [open, place]);

  const toggle = () => {
    if (!open) setView({ year: value.getFullYear(), month: value.getMonth() });
    setOpen((was) => !was);
  };

  const pick = (date) => {
    onChange(date);
    setOpen(false);
    triggerRef.current?.focus();
  };

  // Escape closes the popover only, not the modal around it.
  const handleKeyDown = (event) => {
    if (event.key !== "Escape" || !open) return;
    event.stopPropagation();
    setOpen(false);
    triggerRef.current?.focus();
  };

  return (
    <div onKeyDown={handleKeyDown}>
      <span id="off-shift-date-label" className={`${labelClass} ${requiredMark}`}>
        Date
      </span>
      <button
        ref={triggerRef}
        type="button"
        onClick={toggle}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-labelledby="off-shift-date-label off-shift-date-value"
        className={`${fieldShell} text-left ${bareControl} focus-visible:border-orange-primary focus-visible:ring-2 focus-visible:ring-orange-primary/20 ${
          open ? "border-orange-primary ring-2 ring-orange-primary/20" : "border-gray-200 hover:border-gray-300"
        }`}
      >
        <CalendarDays className="h-4 w-4 shrink-0 text-orange-primary" aria-hidden="true" />
        <span id="off-shift-date-value" className="min-w-0 flex-1 truncate text-bc-text">
          {formatDayHeading(value)}
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-bc-faint transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>

      {open ? (
        <div
          ref={popoverRef}
          role="dialog"
          aria-label="Pick a date"
          style={{ top: position?.top ?? -9999, left: position?.left ?? -9999 }}
          className="fixed z-10 w-72 rounded-xl border border-bc-hairline bg-white p-3 shadow-xl motion-safe:animate-popover-in"
        >
          <SlotMonthCalendar
            year={view.year}
            month={view.month}
            onMonthChange={(year, month) => setView({ year, month })}
            selectedDate={value}
            onSelectDate={pick}
            markedByDate={{}}
            allowClosed
            showLegend={false}
          />
          <div className="mt-2 flex items-center justify-between px-1 text-xs">
            <span className="text-bc-soft">Past days can&apos;t be booked</span>
            <button
              type="button"
              onClick={() => pick(today)}
              className="rounded font-semibold text-orange-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary"
            >
              Today
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

/**
 * Booking a call **outside** a coach's shift hours.
 *
 * Shift hours are cut into the slots clients book, so a coach books their own
 * extra calls around them: a client, a day, and a start time. The call type
 * isn't chosen — the API sizes every call by the client's call number and
 * refuses any other — so it is read for the picked client and shown, and its
 * length sets the end time, which is never typed.
 *
 * A client who already has a confirmed call that day can't be given an
 * off-shift one too: the picker reads their calls for the picked day and
 * refuses the booking, naming the call.
 *
 * Any window touching a shift in effect that day is refused here, before the
 * API sees it. The API accepts an off-shift ADD from the calendar's coach,
 * their manager and executives alike; any refusal it does send is shown in the
 * alert in its own words.
 *
 * Start is the diet intake-time wheel (`IntakeTimePicker`), 24-hour like End.
 *
 * Mount only while open: every open starts clean. It animates in on mount,
 * and every way out — Cancel, ×, Escape, outside click, a booking — plays the
 * exit before `onClose` / `onBooked` is called.
 *
 * @param {Object} props
 * @param {() => void} props.onClose
 * @param {string} props.coachId - The calendar's `employee_id`.
 * @param {string} [props.coachName] - Unset on the caller's own calendar.
 * @param {boolean} props.ownCalendar
 * @param {string} [props.departmentTitle]
 * @param {string|null} [props.departmentId]
 * @param {Array<{ title: string, id: string }>} [props.departmentChoices] -
 *   Offered when the API asks which department the call is for.
 * @param {string} [props.coachShiftTypeId]
 * @param {Object} props.shifts - The workspace's `useCoachShifts()` result.
 * @param {Map<string, string>} [props.callTypeLabels] - Label by call type id.
 * @param {Date|null} [props.initialDate]
 * @param {(booked: { startsAt: Date, userName: string }) => void} props.onBooked
 */
export const OffShiftBookingModal = ({
  onClose,
  coachId,
  coachName,
  ownCalendar,
  departmentTitle,
  departmentId,
  departmentChoices = [],
  coachShiftTypeId,
  shifts,
  callTypeLabels,
  initialDate,
  onBooked,
}) => {
  const today = useMemo(() => localDayStart(new Date()), []);
  const [dateValue, setDateValue] = useState(() => {
    const start = initialDate && localDayStart(initialDate) > today ? initialDate : today;
    return formatLocalDate(start);
  });
  const [client, setClient] = useState(null);
  const [startValue, setStartValue] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [clientTotal, setClientTotal] = useState(null);

  /* --------------------------------- data -------------------------------- */

  const date = parseLocalDate(dateValue);
  const data = useOffShiftBooking({
    coachId,
    coachName,
    ownCalendar,
    departmentTitle,
    departmentId,
    coachShiftTypeId,
    userId: client?.userId || null,
    date,
  });
  // While the booking is in flight nothing closes the modal — not Cancel, ×,
  // Escape or the backdrop — so its outcome is seen.
  const { leaving, close, leaveThen } = useModalExit({ onClose, blocked: data.saving });

  // "12 of 42 clients": the total is whatever the unsearched list last held.
  const searching = Boolean(data.search.trim());
  useEffect(() => {
    if (!searching && !data.clientsLoading && !data.clientsError) setClientTotal(data.clients.length);
  }, [searching, data.clientsLoading, data.clientsError, data.clients]);

  const duration = data.callType?.durationMinutes;
  const startMinutes = parseTimeString(startValue);
  const endMinutes = Number.isFinite(startMinutes) && duration ? startMinutes + duration : null;
  const pastMidnight = Number.isFinite(endMinutes) && endMinutes > MINUTES_PER_DAY;
  const dayShifts = date && shifts?.hasData ? shifts.shiftsForDate(date) : [];
  const windowError = pastMidnight
    ? { kind: "midnight" }
    : offShiftWindowError({ date, startMinutes, endMinutes, dayShifts });

  const whose = ownCalendar ? "your" : `${coachName || "this coach"}'s`;
  const theirs = ownCalendar ? "your" : "their";

  const pickClient = (next) => {
    setClient(next);
    setSubmitError("");
  };

  const callTypeLabel = data.callType
    ? callTypeLabels?.get(String(data.callType.shiftCallTypeId)) ||
      (Number.isInteger(data.callType.callNo) ? `Call ${data.callType.callNo + 1}` : "Call")
    : null;

  const confirmedThatDay = data.dayCalls?.[0] || null;
  const canSubmit =
    Boolean(client && data.callType && shifts?.hasData && data.dayCalls) &&
    !confirmedThatDay &&
    !windowError &&
    !data.saving &&
    !leaving;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;
    // Re-checked on a fresh clock: a form left open can drift into the past.
    if (offShiftWindowError({ date, startMinutes, endMinutes, dayShifts, now: new Date() })) return;
    const { startsAt, endsAt } = windowToInstants(date, startMinutes, endMinutes);
    setSubmitError("");
    try {
      await data.book({ startsAt, endsAt });
      const userName = client.name;
      leaveThen(() => onBooked({ startsAt, userName }));
    } catch (err) {
      setSubmitError(err?.message || "Couldn't book this call.");
    }
  };

  const windowMessage = (() => {
    if (!windowError) return null;
    if (windowError.kind === "midnight") return "This call would run past midnight. Pick an earlier start.";
    if (windowError.kind === "order") return "The end time must be after the start time.";
    if (windowError.kind === "past") return "That time has already passed. Pick a later one.";
    if (windowError.kind === "shift") {
      const { shift } = windowError;
      return `${formatMinutesRange(startMinutes, endMinutes)} falls inside ${whose} shift ${formatMinutesRange(
        shift.startMinutes,
        shift.endMinutes
      )}. Clients book shift slots; pick a time outside ${theirs} shift hours.`;
    }
    return null;
  })();

  const startFlagged = ["shift", "past", "midnight"].includes(windowError?.kind);

  const countLabel = (() => {
    if (data.clientsLoading || data.clientsError) return null;
    const shown = data.clients.length;
    const total = searching && clientTotal !== null ? clientTotal : shown;
    return `${shown} of ${total} client${total === 1 ? "" : "s"}`;
  })();

  const footer = (
    <div className="flex justify-end gap-2">
      <button
        type="button"
        onClick={close}
        disabled={data.saving}
        className="min-h-[40px] rounded-lg border border-gray-200 px-5 text-sm font-semibold text-bc-text-2 transition-colors hover:bg-light-gray/60 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary"
      >
        Cancel
      </button>
      <button
        type="submit"
        form="off-shift-booking-form"
        disabled={!canSubmit}
        className="min-h-[40px] rounded-lg bg-orange-primary px-5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-orange-primary/90 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary focus-visible:ring-offset-1"
      >
        {data.saving ? "Booking…" : "Book call"}
      </button>
    </div>
  );

  return (
    <Modal
      isOpen
      onClose={close}
      size="md"
      animated
      leaving={leaving}
      closeDisabled={data.saving}
      title="Book off-shift call"
      subtitle={
        ownCalendar
          ? "On your calendar, outside your shift hours"
          : `For ${coachName || "this coach"}, outside their shift hours`
      }
      footer={footer}
    >
      <form id="off-shift-booking-form" onSubmit={handleSubmit} className="space-y-5">
        {/* Client */}
        <fieldset aria-labelledby="off-shift-client-label">
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <span id="off-shift-client-label" className="text-xs font-semibold text-bc-text">
              Client
            </span>
            {countLabel ? <span className="text-xs text-bc-soft">{countLabel}</span> : null}
          </div>
          <div className={`${fieldShell} border-gray-200`}>
            <Search className="h-4 w-4 shrink-0 text-bc-faint" aria-hidden="true" />
            <input
              type="search"
              value={data.search}
              onChange={(e) => data.setSearch(e.target.value)}
              placeholder="Search name, phone or email"
              aria-label="Search clients"
              className={`h-full min-w-0 flex-1 bg-transparent text-sm text-bc-text placeholder:text-bc-faint ${bareControl}`}
            />
          </div>
          <div className="mt-3 max-h-56 overflow-y-auto rounded-xl border border-bc-hairline">
            {data.clientsError ? (
              <div className="p-2">
                <RetryBanner message={data.clientsError.message} onRetry={data.retryClients} />
              </div>
            ) : data.clientsLoading ? (
              <div className="p-3">
                <SkeletonRows count={3} />
              </div>
            ) : data.clients.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-bc-soft">
                {searching
                  ? "No matching clients."
                  : ownCalendar
                  ? "You have no clients assigned."
                  : "No clients are assigned to this coach."}
              </p>
            ) : (
              <ul className="divide-y divide-bc-hairline">
                {data.clients.map((row) => {
                  const selected = String(client?.userId) === String(row.userId);
                  const contact = [row.phone, row.email].filter(Boolean).join(" · ");
                  const joined = joinedAgo(row.joinedAt);
                  return (
                    <li key={row.userId}>
                      <label
                        className={`flex cursor-pointer items-center gap-3 px-3 py-2.5 text-sm transition-colors focus-within:bg-light-orange/60 ${
                          selected ? "bg-light-orange" : "hover:bg-bc-subtle"
                        }`}
                      >
                        <input
                          type="radio"
                          name="off-shift-client"
                          className="sr-only"
                          checked={selected}
                          onChange={() => pickClient(row)}
                        />
                        <span
                          aria-hidden="true"
                          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${avatarTone(
                            row.userId
                          )}`}
                        >
                          {initialsOf(row.name)}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-semibold text-bc-text">{row.name}</span>
                          <span className="block truncate text-xs text-bc-soft">
                            {contact || "No contact details"}
                          </span>
                        </span>
                        {joined ? (
                          <span className="shrink-0 text-xs text-bc-soft" title="Joined">
                            {joined}
                          </span>
                        ) : null}
                        <span
                          aria-hidden="true"
                          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                            selected ? "border-orange-primary" : "border-gray-300"
                          }`}
                        >
                          <span
                            className={`h-2.5 w-2.5 rounded-full bg-orange-primary transition-transform duration-150 ${
                              selected ? "scale-100" : "scale-0"
                            }`}
                          />
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </fieldset>

        {/* Call type */}
        <div>
          <span className={labelClass}>Call type</span>
          {!client ? (
            <p className="text-sm text-bc-soft">Pick a client to see their next call type.</p>
          ) : data.needsDepartment ? (
            <div>
              <p className="text-sm text-bc-text-2">
                {ownCalendar ? "You coach" : `${coachName || "This coach"} coaches`} this client for
                more than one department. Pick which call this is.
              </p>
              <div className="mt-2 flex flex-wrap gap-2" role="group" aria-label="Call for">
                {departmentChoices.map((choice) => (
                  <FilterChip
                    key={choice.id}
                    label={choice.title}
                    active={data.pickedDepartmentId === choice.id}
                    onClick={() => data.setDepartmentId(choice.id)}
                  />
                ))}
              </div>
            </div>
          ) : data.callTypeError ? (
            <RetryBanner message={data.callTypeError.message} onRetry={data.retryCallType} />
          ) : data.callTypeLoading || !data.callType ? (
            <SkeletonRows count={1} />
          ) : (
            <p className="flex flex-wrap items-center gap-2 text-sm text-bc-text" data-testid="off-shift-call-type">
              <span>
                {client.name} · {callTypeLabel}
              </span>
              {duration ? (
                <span className="rounded-full bg-light-orange px-2 py-0.5 text-xs font-semibold text-bc-accent-text">
                  {duration} min
                </span>
              ) : null}
            </p>
          )}
        </div>

        {/* Date and time */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <DateField
            value={date || today}
            today={today}
            onChange={(next) => {
              setDateValue(formatLocalDate(next));
              setSubmitError("");
            }}
          />

          <div>
            <label htmlFor="off-shift-start" className={`${labelClass} ${requiredMark}`}>
              Start
            </label>
            <IntakeTimePicker
              id="off-shift-start"
              value={startValue}
              onChange={(value) => {
                setStartValue(value);
                setSubmitError("");
              }}
              placeholder="--:--"
              defaultTime="09:00"
              panelLabel="Choose start time"
              className={`h-10 w-full ${startFlagged ? "!border-amber-400" : ""}`}
            />
          </div>

          <div>
            <label htmlFor="off-shift-end" className={labelClass}>
              End
            </label>
            <div className={`${fieldShell} border-gray-200 bg-bc-subtle`}>
              <Clock className="h-4 w-4 shrink-0 text-bc-faintest" aria-hidden="true" />
              <input
                id="off-shift-end"
                readOnly
                tabIndex={-1}
                value={Number.isFinite(endMinutes) && !pastMidnight ? toHHMM(endMinutes) : ""}
                placeholder="--:--"
                title="Set by the call type's length"
                className={`h-full min-w-0 flex-1 cursor-default bg-transparent text-sm tabular-nums text-bc-text-2 placeholder:text-bc-faint ${bareControl}`}
              />
              <span className="shrink-0 rounded bg-light-gray px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-bc-soft">
                AUTO
              </span>
            </div>
          </div>
        </div>

        {!shifts?.hasData ? (
          <SkeletonRows count={1} />
        ) : date ? (
          <p className="text-xs text-bc-soft">
            {dayShifts.length === 0
              ? `No shift on ${formatShortDate(date)}, so any time that day is outside ${theirs} shift hours.`
              : `Shift hours on ${formatShortDate(date)}: ${dayShifts
                  .map((shift) => formatMinutesRange(shift.startMinutes, shift.endMinutes))
                  .join(", ")}. Book outside these.`}
          </p>
        ) : null}

        {client && data.dayCallsError ? (
          <RetryBanner
            message={data.dayCallsError.message || "Couldn't check this client's calls that day."}
            onRetry={data.retryDayCalls}
          />
        ) : null}

        {confirmedThatDay ? (
          <p role="alert" className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
            {`${client.name} already has a confirmed call on ${formatShortDate(date)}, ${formatSlotRange({
              startsAt: confirmedThatDay.start_time,
              end_time: confirmedThatDay.end_time,
            })}${
              confirmedThatDay.coach_name ? ` with ${confirmedThatDay.coach_name}` : ""
            }. Pick another day.`}
          </p>
        ) : windowMessage ? (
          <p role="alert" className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
            {windowMessage}
          </p>
        ) : null}

        {submitError ? (
          <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {submitError}
          </p>
        ) : null}
      </form>
    </Modal>
  );
};
